/**
 * ¿La sesión ha muerto, o solo no se pudo refrescar el token ahora?
 * (spec 2026-10-01 § 5.2)
 *
 * Es la ÚNICA decisión de «cerrar sesión» de la app, y se toma solo sobre el
 * error de la propia llamada de refresco. Un FORBIDDEN de cualquier otra
 * consulta es de permisos y no pasa por aquí (RFC 9110 §15.5.4): entrar sin
 * querer en algo prohibido nunca saca a nadie al login.
 *
 * Se decide por CÓDIGO (Supabase, Microsoft); el texto solo se mira si el back
 * es anterior a los códigos (transición).
 */

/** Byte a byte con `rois-back/src/modules/auth/auth.constants.ts`. */
export const DEAD_SESSION_CODES = [
  'AUTH_REFRESH_EXPIRED',
  'AUTH_USER_NOT_FOUND',
  'AUTH_USER_INACTIVE',
  'AUTH_NO_ACTIVE_TENANT',
] as const;

export type DeadSessionReason = (typeof DEAD_SESSION_CODES)[number];

/**
 * Transición: un back sin códigos (anterior a la Fase 1a) solo manda el texto.
 * Se puede borrar cuando el back de prod lleve los códigos.
 */
const LEGACY_DEAD_SESSION_MESSAGES: Readonly<Record<string, DeadSessionReason>> = {
  'Refresh token expire': 'AUTH_REFRESH_EXPIRED',
  'User not found or inactive': 'AUTH_USER_INACTIVE',
  'User does not have an active tenant configured': 'AUTH_NO_ACTIVE_TENANT',
};

/** Los textos de transición, para quien los reconozca sin pasar por el código. */
export const LEGACY_DEAD_SESSION_TEXTS: readonly string[] = Object.keys(LEGACY_DEAD_SESSION_MESSAGES);

export type RefreshFailure =
  | { kind: 'session-ended'; reason: DeadSessionReason }
  | { kind: 'unavailable' };

type GraphQLErrorLike = {
  message?: string;
  extensions?: { code?: string; originalError?: { code?: string } };
};

function isDeadSessionCode(code: unknown): code is DeadSessionReason {
  return (
    typeof code === 'string' && (DEAD_SESSION_CODES as readonly string[]).includes(code)
  );
}

export function classifyRefreshFailure(error: unknown): RefreshFailure {
  const gqlErrors =
    (error as { graphQLErrors?: GraphQLErrorLike[] } | null | undefined)?.graphQLErrors ?? [];
  for (const g of gqlErrors) {
    if (g?.extensions?.code !== 'FORBIDDEN') continue;
    const code = g.extensions.originalError?.code;
    if (isDeadSessionCode(code)) return { kind: 'session-ended', reason: code };
    // Con código manda el código: el texto solo se mira si no lo hay.
    const legacy = !code && g.message ? LEGACY_DEAD_SESSION_MESSAGES[g.message] : undefined;
    if (legacy) return { kind: 'session-ended', reason: legacy };
  }
  // Red, plazo, 5xx, error técnico, otro FORBIDDEN: no dice nada de la sesión.
  return { kind: 'unavailable' };
}

/** La sesión ha terminado de verdad: la cola conserva la operación y pide login. */
export class SessionEndedError extends Error {
  readonly reason: DeadSessionReason;

  constructor(reason: DeadSessionReason) {
    super(`La sesión ha terminado (${reason})`);
    this.name = 'SessionEndedError';
    this.reason = reason;
  }
}

/** No se pudo refrescar ahora (red, plazo, 5xx): la sesión sigue viva. */
export class RefreshUnavailableError extends Error {
  readonly original: unknown;

  constructor(original: unknown) {
    super('No se pudo refrescar el token ahora; la sesión se conserva');
    this.name = 'RefreshUnavailableError';
    this.original = original;
  }
}

/**
 * La sesión cambió con la operación en curso (logout + login de otro, o
 * switchTenant): durante el refresco, o entre un intento y su reintento. No es
 * un fallo de red: la operación era de OTRA sesión y no puede salir con la
 * nueva. Una escritura se encola con su dueño de origen (spec § 12.1, § 12.11).
 */
export class SessionChangedError extends Error {
  constructor() {
    super('La sesión cambió con la operación en curso');
    this.name = 'SessionChangedError';
  }
}

// Por `name` y no por `instanceof`: Apollo envuelve el error de un link en
// `networkError`, y las colas lo leen después de pasar por IndexedDB.
function hasErrorName(error: unknown, name: string): boolean {
  const e = error as { name?: string; networkError?: { name?: string } } | null | undefined;
  return e?.name === name || e?.networkError?.name === name;
}

export const isSessionEndedError = (error: unknown): boolean =>
  hasErrorName(error, 'SessionEndedError');

export const isRefreshUnavailableError = (error: unknown): boolean =>
  hasErrorName(error, 'RefreshUnavailableError');

export const isSessionChangedError = (error: unknown): boolean =>
  hasErrorName(error, 'SessionChangedError');
